/**
 * Claim generation on the device.
 *
 * This device scores its own recording against its owner's template and nothing
 * else -- it holds no other template and could not score anyone else if it wanted
 * to. That restriction is the whole architecture in one function, and it is why
 * the floor here is 0.25 rather than the 0.328 a single-device diarizer needs:
 * the absolute score only has to clear a low bar, and the cross-device comparison
 * on the server does the discriminating.
 *
 * What leaves the device is a list of `{start, end, confidence}` intervals plus a
 * 50 Hz energy envelope. Not embeddings -- those *are* the template.
 */

import * as K from "./const.js";
import { cosine, embedMany } from "./ecapa.js";
import { speechRegions, wireEnvelope, bytesToBase64 } from "./audio.js";

/** Window start times covering a speech region, including a tail window. */
function windowStarts(regionStart, regionEnd, hopSec = K.HOP_SEC) {
  const span = regionEnd - regionStart;
  if (span <= 0) return [];
  if (span <= K.WINDOW_SEC) return [regionStart];
  const n = Math.floor((span - K.WINDOW_SEC) / hopSec) + 1;
  const starts = [];
  for (let i = 0; i < n; i++) starts.push(regionStart + i * hopSec);
  // Without a tail window, up to a hop of the end of every turn is never scored,
  // which costs real attribution at handovers.
  const tail = regionEnd - K.WINDOW_SEC;
  if (tail > starts[starts.length - 1] + 1e-6) starts.push(tail);
  return starts;
}

/**
 * Score one span of recorded audio.
 *
 * `offsetSec` is where this span sits in the device's own recording, so the
 * returned frame indices are on the device clock rather than the span's.
 */
export async function scoreSpan(wav, template, offsetSec, onProgress, hopSec = K.HOP_SEC) {
  const regions = speechRegions(wav);
  const chunks = [];
  const spans = [];
  const windowSamples = Math.round(K.WINDOW_SEC * K.SAMPLE_RATE);

  for (const [rStart, rEnd] of regions) {
    for (const wStart of windowStarts(rStart, rEnd, hopSec)) {
      const a = Math.round(wStart * K.SAMPLE_RATE);
      const chunk = wav.subarray(a, a + windowSamples);
      // Under a second is noise, not a weak claim. Dropping it is honest.
      if (chunk.length < K.SAMPLE_RATE) continue;
      chunks.push(chunk);
      spans.push([offsetSec + wStart, offsetSec + wStart + chunk.length / K.SAMPLE_RATE]);
    }
  }
  if (!chunks.length) return { frames: new Map(), windows: 0, speechSec: 0 };

  // Reported per batch rather than once at the end. This is the slowest thing a device
  // does, and on the final chunk somebody is watching a button while it happens.
  const vectors = await embedMany(chunks, undefined, onProgress);
  onProgress?.(1);

  // Each window votes for every frame it covers, and a frame takes the strongest
  // vote. Attributing a 2 s window only to its start frame would drop the last
  // second of every turn; only to its centre would drop the first.
  const frames = new Map();
  vectors.forEach((vec, i) => {
    const score = cosine(vec, template);
    const [start, end] = spans[i];
    const first = Math.floor(start / K.FRAME_SEC + 1e-9);
    const last = Math.ceil(end / K.FRAME_SEC - 1e-9) - 1;
    for (let f = Math.max(0, first); f <= Math.max(0, last); f++) {
      if (!frames.has(f) || frames.get(f) < score) frames.set(f, score);
    }
  });

  return {
    frames,
    windows: chunks.length,
    speechSec: regions.reduce((sum, [a, b]) => sum + (b - a), 0),
  };
}

/**
 * Per-frame confidence -> wire intervals.
 *
 * Contiguous frames in the same quantised confidence band become one interval.
 * The banding is the one real judgement call in the wire format: collapsing a
 * whole turn to a single averaged confidence would satisfy the trust boundary but
 * would hand the merge one number per turn, so a cross-device argmax during
 * overlap would be decided by two turn-long averages and the closer microphone
 * would win the entire region.
 */
export function collapseToIntervals(frames, floor = K.CLAIM_FLOOR) {
  const indices = [...frames.keys()].sort((a, b) => a - b);
  const intervals = [];
  let runStart = null;
  let runBand = null;
  let runValues = [];

  const flush = (endFrame) => {
    if (runStart === null) return;
    const mean = runValues.reduce((a, b) => a + b, 0) / runValues.length;
    intervals.push({
      start: round3(runStart * K.FRAME_SEC),
      end: round3(endFrame * K.FRAME_SEC),
      confidence: Math.round(mean * 1e4) / 1e4,
    });
  };

  let previous = null;
  for (const f of indices) {
    const conf = frames.get(f);
    if (conf < floor) continue;
    const band = Math.floor(conf / K.CONFIDENCE_BAND);
    const contiguous = previous !== null && f === previous + 1;
    if (runStart === null || !contiguous || band !== runBand) {
      if (runStart !== null) flush(previous + 1);
      runStart = f;
      runBand = band;
      runValues = [conf];
    } else {
      runValues.push(conf);
    }
    previous = f;
  }
  if (runStart !== null) flush(previous + 1);
  return intervals;
}

function round3(x) {
  return Math.round(x * 1000) / 1000;
}

/**
 * Score windows as their audio arrives, instead of saving a chunk's worth for the end.
 *
 * `scoreSpan` above waits for `CLAIM_CHUNK_SEC` of audio and then scores ~20 windows in one
 * burst. That is the same total work either way, but it means that when somebody presses stop
 * a whole chunk is still owed. [M] On one real session the slower of two phones took 9.19 s
 * to work through its last 21 s span, which was most of the 10.54 s it spent reporting itself
 * finished; the same phone was spending ~14 s on every 20 s chunk mid-session, so the work
 * was always affordable -- it was just being saved up for the moment somebody was watching a
 * button. Measured in a browser against the shipped model, moving to one window at a time
 * costs 2-3% in throughput and leaves 52 ms owed at the stop instead of 1010 ms.
 *
 * The window and the hop are untouched: 2 s windows on a 1 s hop, the same grid, the same
 * model. [M] 2 s is the knee and 1 s is not (7.6% error against 0.5%), so nothing here goes
 * near `WINDOW_SEC`.
 *
 * **No VAD gate, deliberately.** `scoreSpan` windows only inside `speechRegions`, and that
 * function's floor is relative to the span it is handed (`db.max()`), so handing it 2 s at a
 * time makes the noise floor its own reference. Measured on a real recording: slicing it that
 * way admits 1.4 s of noise, *loses 2.4 s of speech*, and fragments 20 regions into 62. Not
 * gating at all costs 119 windows against 113 -- 5.3% -- and cannot lose speech, so that is
 * what this does. `collapseToIntervals` still gates on `CLAIM_FLOOR`, and a below-floor frame
 * ends a run exactly as an absent one does, so the intervals this produces have the same shape
 * as before. What it cannot rule out is silence that scores *above* the floor: on the session
 * measured there was no such window to find (0 of 118 windows were entirely non-speech), but
 * that was one talkative conversation, which is why `vadRejected` below is reported.
 */
export function createRollingScorer(template, options = {}) {
  const { hopSec = K.HOP_SEC, embed = embedMany, batchSize = 8 } = options;
  const windowSamples = Math.round(K.WINDOW_SEC * K.SAMPLE_RATE);

  /** grid index -> score. A window's start is `index * hopSec`. */
  const scored = new Map();
  let nextToScore = 0;    // lowest grid index not yet embedded
  let nextToHarvest = 0;  // lowest grid index not yet handed to a bundle

  /**
   * What the rolling pass cost, drained into each bundle's telemetry.
   *
   * Without this the change is invisible in production: the per-bundle `score` event reports a
   * duration, but under continuous scoring almost all the work happens between bundles, so that
   * duration goes to nearly zero and says nothing about whether the device kept up. [M] The one
   * number this investigation most wanted and did not have was per-window cost on the slow
   * handset -- 676 ms against another phone's 108 ms, recoverable only because the burst pass
   * happened to be timed as a unit. `maxCallMs` is separate from the mean because it is the
   * main thread's longest uninterrupted block, which is what a dropped microphone looks like.
   */
  const stats = { calls: 0, windows: 0, totalMs: 0, maxCallMs: 0 };

  const startOf = (i) => i * hopSec;
  const endOf = (i) => i * hopSec + K.WINDOW_SEC;

  return {
    /**
     * Embed and score every grid window whose audio has arrived and is not scored yet.
     *
     * `read(fromSec, toSec)` returns that slice of the recording. Several windows are passed
     * to `embed` together when this falls behind -- a device that stalls catches up at the
     * batch rate rather than one window per call.
     */
    async advance(read, availableSec) {
      const due = [];
      for (let i = nextToScore; endOf(i) <= availableSec; i++) due.push(i);
      if (!due.length) return 0;

      const waves = [];
      const kept = [];
      for (const i of due) {
        const w = read(startOf(i), endOf(i));
        // Same rule as `scoreSpan`: under a second is noise, not a weak claim.
        if (!w || w.length < K.SAMPLE_RATE) continue;
        waves.push(w.length > windowSamples ? w.subarray(0, windowSamples) : w);
        kept.push(i);
      }
      nextToScore = due[due.length - 1] + 1;
      if (!waves.length) return 0;

      const t0 = performance.now();
      const vectors = await embed(waves, batchSize);
      const ms = performance.now() - t0;
      vectors.forEach((v, n) => scored.set(kept[n], cosine(v, template)));

      stats.calls += 1;
      stats.windows += kept.length;
      stats.totalMs += ms;
      if (ms > stats.maxCallMs) stats.maxCallMs = ms;
      return kept.length;
    },

    /**
     * Frames for every scored window not yet claimed, as `scoreSpan` would have returned them.
     *
     * Each window votes on every frame it covers and a frame keeps the strongest vote --
     * identical to `scoreSpan`, and for the same reason: attributing a 2 s window only to its
     * start frame would drop the last second of every turn.
     *
     * Returns `null` when there is nothing new, so a caller can skip building a bundle.
     *
     * `maxWindows` and `maxSpanSec` bound how much one call takes, and they exist because
     * this is **destructive**: whatever it returns is gone from the cache, so a caller that
     * cannot hold the result has already lost it. The continuous claim path has a bounded
     * queue and a per-delta span limit, so it asks for at most one delta's worth at a time
     * and comes back on the next tick for the rest.
     *
     * Both bounds are needed and neither implies the other. `scored` is **sparse**:
     * `advance` skips any grid slot whose audio was too short to embed, so two windows
     * either side of a long unscorable stretch are two windows and several minutes. A count
     * bound alone would hand that whole span back, and the caller would be holding an
     * oversized harvest it is not allowed to either keep or drop.
     *
     * Unlimited is the default and is exactly the behaviour the legacy bundle path has
     * always had, down to how `nextToHarvest` moves: an unlimited harvest skips past grid
     * slots that were never scored, while a bounded one stops at the last window it actually
     * took, because the slots beyond it have not been offered to anybody yet.
     */
    harvest(maxWindows = Infinity, maxSpanSec = Infinity) {
      const take = [];
      let truncated = false;
      for (let i = nextToHarvest; i < nextToScore; i++) {
        if (!scored.has(i)) continue;
        // Span first, and measured from the first window actually taken rather than from
        // the cursor: the gap this is guarding against sits *between* scored slots, so a
        // check against `i` alone would never see it. The first window is always taken --
        // one window is 2 s and no caller asks for less than that.
        if (take.length && endOf(i) - startOf(take[0]) > maxSpanSec) {
          truncated = true;
          break;
        }
        take.push(i);
        if (take.length >= maxWindows) {
          truncated = true;
          break;
        }
      }
      if (!take.length) return null;
      const limited = truncated;

      const frames = new Map();
      for (const i of take) {
        const score = scored.get(i);
        const first = Math.floor(startOf(i) / K.FRAME_SEC + 1e-9);
        const last = Math.ceil(endOf(i) / K.FRAME_SEC - 1e-9) - 1;
        for (let f = Math.max(0, first); f <= Math.max(0, last); f++) {
          if (!frames.has(f) || frames.get(f) < score) frames.set(f, score);
        }
      }

      const fromSec = startOf(take[0]);
      const toSec = endOf(take[take.length - 1]);
      nextToHarvest = limited ? take[take.length - 1] + 1 : nextToScore;
      for (const i of [...scored.keys()]) if (i < nextToHarvest) scored.delete(i);
      return { frames, windows: take.length, fromSec: round3(fromSec), toSec: round3(toSec) };
    },

    /** Windows owed at this instant -- what a stop press would have to wait for. */
    pending(availableSec) {
      let n = 0;
      for (let i = nextToScore; endOf(i) <= availableSec; i++) n++;
      return n;
    },

    /**
     * Read the rolling cost since the last drain, and reset it.
     *
     * Rounded here rather than at the call site so every consumer reports the same numbers,
     * and drained rather than cumulative so one bundle's figures describe one bundle's span.
     */
    drainStats() {
      const out = {
        roll_calls: stats.calls,
        roll_windows: stats.windows,
        roll_ms: Math.round(stats.totalMs),
        roll_ms_per_window: stats.windows ? Math.round(stats.totalMs / stats.windows) : 0,
        roll_max_call_ms: Math.round(stats.maxCallMs),
      };
      stats.calls = 0;
      stats.windows = 0;
      stats.totalMs = 0;
      stats.maxCallMs = 0;
      return out;
    },
  };
}

/**
 * Assemble one continuous claim-delta payload (#170-A).
 *
 * The unit is a *harvest*, not a chunk: whatever the rolling scorer has finished at
 * this instant, which is what takes the cadence below the legacy 20 s semantic bundle.
 *
 * This is the only browser claim payload. Its wire format is intentionally independent
 * of the retired signed bundle protocol: a delta carries
 * `protocol`/`timeline_epoch`/`delta_id`/`delta_sequence`/`operation`/`claim_id` and
 * carries **no** `version` and **no** `nonce`, and the server's model is strict, so one
 * stray legacy field is a 422 on a signed payload that can never be repaired.
 *
 * Only `assert` is emitted in this slice. `assert` forbids `base_sequence`,
 * `base_digest` and `target_claim_id`, so they are omitted rather than sent as nulls --
 * scorer-generated overlap corrections (`replace`/`retract`) are a later ticket, and
 * the transport carries them already.
 *
 * `localStartSec`/`localEndSec` must describe exactly the span the envelope, the
 * intervals and the capture gaps were taken from: the server places the envelope by
 * `local_start_sec` and validates that no interval starts before it or ends more than a
 * frame past `local_end_sec`.
 */
export function buildClaimDeltaPayload({
  sessionId,
  claimProfileId,
  participantId,
  timelineEpoch,
  deltaId,
  deltaSequence,
  claimId,
  intervals,
  envelopeBytes,
  localStartSec,
  localEndSec,
  recordingStartedAt,
  captureGaps,
}) {
  return {
    protocol: K.CLAIM_DELTA_PROTOCOL_VERSION,
    session_id: sessionId,
    claim_profile_id: claimProfileId,
    participant_id: participantId,
    timeline_epoch: timelineEpoch,
    delta_id: deltaId,
    delta_sequence: deltaSequence,
    operation: "assert",
    claim_id: claimId,
    created_at: new Date().toISOString(),
    recording_started_at: recordingStartedAt,
    local_start_sec: round3(localStartSec),
    local_end_sec: round3(localEndSec),
    frame_sec: K.FRAME_SEC,
    floor: K.CLAIM_FLOOR,
    intervals,
    envelope: envelopeBytes ? bytesToBase64(envelopeBytes) : null,
    envelope_rate_hz: K.ENVELOPE_WIRE_RATE_HZ,
    capture_gaps: (captureGaps || []).map((g) => ({ start: round3(g.start), end: round3(g.end) })),
    // Same string the legacy bundle carries, for the same reason: it is the only record
    // of which build produced a signed payload.
    generator: `meetfolks-pwa/0.1.0+${globalThis.__MEETFOLKS_BUILD__ ?? "dev"}`,
    model: "speechbrain/spkrec-ecapa-voxceleb",
  };
}

export { wireEnvelope };
